import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Icon from './icons';
import { tx } from './i18n';

const API = process.env.REACT_APP_API_URL || '';

// ── Sterowanie WSJT-X/JTDX przez agenta HamLoggera ───────────────────────────
// Polecenia ida kolejka na serwer, agent (na komputerze przy radiu) odsyla je
// do WSJT-X po UDP. Status (tryb, TX, konfiguracja) raportuje agent.
// Odpowiedz stacji i nadanie wolnego tekstu uruchamiaja nadajnik -- dzialaja
// tylko, gdy agent ma w konfiguracji allow_tx_control = true.

const FT_MODES = ['FT8', 'FT4', 'JT65', 'JT9', 'Q65', 'MSK144', 'FST4', 'WSPR'];

// Wyniki juz pokazane uzytkownikowi (wspolne dla sendWsjtx i useWsjtxStatus -- bez dublowania)
const notifiedResults = new Set();

// Wysyla polecenie i CZEKA na potwierdzenie od agenta (agent sprawdza po statusie
// WSJT-X, czy polecenie naprawde zadzialalo). Zwraca true / false / null (brak
// potwierdzenia w 15 s).
export async function sendWsjtx(action, payload, notify, { wait = true } = {}) {
  let id;
  try {
    const r = await axios.post(`${API}/api/wsjtx/control`, { action, ...payload });
    id = r.data?.id;
  } catch (err) {
    notify?.(err.response?.data?.error || tx('Nie udało się wysłać polecenia do WSJT-X'), 'error');
    return false;
  }
  if (!wait || !id) return true;
  const end = Date.now() + 15000;
  while (Date.now() < end) {
    await new Promise(res => setTimeout(res, 1200));
    try {
      const r = await axios.get(`${API}/api/wsjtx/control/status`);
      const res = (r.data.results || []).find(x => x.id === id);
      if (res) {
        notifiedResults.add(res.id + res.message);
        notify?.(`WSJT-X: ${res.message}`, res.ok ? 'success' : 'error', res.ok ? undefined : 15000);
        return !!res.ok;
      }
    } catch { /* sprobuj ponownie */ }
  }
  notify?.(tx('Agent nie potwierdził wykonania polecenia w 15 s — sprawdź, czy agent działa (Narzędzia → Diagnostyka agenta).'), 'warning', 10000);
  return null;
}

// Status agenta; wyniki polecen pokazywane jako powiadomienia (kazdy raz)
export function useWsjtxStatus(notify, intervalMs = 10000, enabled = true) {
  const [st, setSt] = useState(null);
  const seen = useRef(new Set());
  const first = useRef(true);
  const load = useCallback(() => {
    axios.get(`${API}/api/wsjtx/control/status`).then(r => {
      setSt(r.data);
      for (const res of r.data.results || []) {
        if (seen.current.has(res.id + res.message) || notifiedResults.has(res.id + res.message)) continue;
        seen.current.add(res.id + res.message);
        notifiedResults.add(res.id + res.message);
        if (!first.current) notify?.(`WSJT-X: ${res.message}`, res.ok ? 'success' : 'error');
      }
      first.current = false;
    }).catch(() => setSt(s => s || { online: false }));
  }, [notify]);
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [load, intervalMs, enabled]);
  return [st, load];
}

// Przycisk "Odpowiedz" (jak dwuklik na dekodzie w WSJT-X)
export function WsjtxReplyButton({ callsign, wsjtx, notify, onSent, compact }) {
  if (!wsjtx?.online || !callsign) return null;
  const allowed = !!wsjtx.status?.tx_control_allowed;
  return (
    <button type="button" className={compact ? 'btn-icon' : 'btn btn--secondary btn--sm'}
      title={allowed ? tx('Odpowiedz {0} w WSJT-X (uruchamia nadawanie)', [callsign]) : tx('Odpowiadanie z HamLoggera jest wyłączone w agencie (allow_tx_control = false)')}
      disabled={!allowed}
      onClick={async () => { notify?.(tx('Wysyłam do WSJT-X: {0}…', [callsign])); if ((await sendWsjtx('reply', { callsign }, notify)) !== false) onSent?.(); }}>
      <Icon name="send" size={compact ? 16 : 14} />{!compact && (' ' + tx('Odpowiedz') )}
    </button>
  );
}

// Panel w widzecie radia
const openDiagnostics = () => window.dispatchEvent(new CustomEvent('hamlogger:navigate', { detail: 'agent-diag' }));
const openRemote = () => window.dispatchEvent(new CustomEvent('hamlogger:navigate', { detail: 'wsjtx-remote' }));

export function WsjtxPanel({ notify, remote }) {
  const [wsjtx, reload] = useWsjtxStatus(notify, 4000);
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('wsjtx_panel_open') !== '0'; } catch { return true; } });
  const [text, setText] = useState('');
  const [config, setConfig] = useState('');
  const toggle = () => { const v = !open; setOpen(v); try { localStorage.setItem('wsjtx_panel_open', v ? '1' : '0'); } catch { /* brak storage */ } };
  const st = wsjtx?.status;
  const allowed = !!st?.tx_control_allowed;
  const send = async (action, payload) => { if ((await sendWsjtx(action, payload, notify)) !== false) setTimeout(reload, 500); };
  const setHl = async (patch) => {
    try { await axios.put(`${API}/api/wsjtx/highlight-settings`, patch); reload(); }
    catch { notify?.(tx('Nie zapisano ustawień podświetlania'), 'error'); }
  };

  return (
    <div className="wsjtx-panel">
      <button type="button" className="wsjtx-panel__head" onClick={toggle} aria-expanded={open}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
        <span>WSJT-X</span>
        <span className={'wsjtx-panel__state' + (wsjtx?.online ? ' wsjtx-panel__state--ok' : '')}>
          {!wsjtx ? '…' : !wsjtx.online ? tx('niepołączony') : st?.transmitting ? 'NADAJE' : st?.tx_enabled ? tx('TX włączony') : tx('odbiór')}
        </span>
      </button>
      {open && (!wsjtx?.online ? (
        <div className="wsjtx-panel__hint">
          {tx('Uruchom agenta HamLoggera i WSJT-X/JTDX na komputerze przy radiu (UDP 2237). Status pojawi się tutaj.')}
          <button type="button" className="btn btn--ghost btn--sm wsjtx-panel__diag" onClick={openDiagnostics}>
            <Icon name="bug" size={14} /> {tx('Diagnostyka agenta')}
          </button>
        </div>
      ) : (
        <div className="wsjtx-panel__body">
          <div className="wsjtx-panel__status">
            {st?.mode || '?'} · {st?.dial_freq_hz ? (st.dial_freq_hz / 1e6).toFixed(3) + (' ' + tx('MHz') ) : '—'}
            {st?.config_name ? ` · ${st.config_name}` : ''}{st?.dx_call ? ` · DX ${st.dx_call}` : ''}
          </div>
          {st?.tx_message && <div className="wsjtx-panel__tx">TX: {st.tx_message}</div>}
          <button type="button" className={'btn btn--sm wsjtx-panel__stop' + (st?.transmitting || st?.tx_enabled ? ' wsjtx-panel__stop--live' : '')}
            onClick={() => send('halt', {})}>
            <Icon name="x" size={14} /> {tx('Stop TX')}
          </button>
          <div className="wsjtx-panel__row">
            <select className="input" value="" onChange={e => e.target.value && send('mode', { mode: e.target.value })} aria-label={tx('Tryb WSJT-X')}>
              <option value="">{tx('Zmień tryb…')}</option>
              {(wsjtx.modes || FT_MODES).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => send('clear', { window: 2 })} title={tx('Wyczyść okna dekodów')}>{tx('Wyczyść')}</button>
          </div>
          <div className="wsjtx-panel__row">
            <input className="input" value={config} onChange={e => setConfig(e.target.value)} placeholder={tx('Nazwa konfiguracji')} maxLength={60} />
            <button type="button" className="btn btn--ghost btn--sm" disabled={!config.trim()} onClick={() => send('config', { name: config.trim() })}>{tx('Przełącz')}</button>
          </div>
          <div className="wsjtx-panel__row">
            <input className="input" value={text} onChange={e => setText(e.target.value.toUpperCase().slice(0, 13))} placeholder={tx('Wolny tekst (13 zn.)')} />
            <button type="button" className="btn btn--ghost btn--sm" disabled={!text.trim()} onClick={() => send('freetext', { text, send: false })}>{tx('Ustaw')}</button>
            <button type="button" className="btn btn--secondary btn--sm" disabled={!text.trim() || !allowed}
              title={allowed ? tx('Ustaw i nadaj w najbliższym okresie TX') : tx('Nadawanie z HamLoggera wyłączone w agencie (allow_tx_control = false)')}
              onClick={() => send('freetext', { text, send: true })}>{tx('Nadaj')}</button>
          </div>
          <div className="wsjtx-panel__hl">
            <span>{tx('Podświetlaj w WSJT-X:')}</span>
            <label className="checkbox-label"><input type="checkbox" checked={!!wsjtx.highlight?.watchlist}
              onChange={e => setHl({ watchlist: e.target.checked })} /> <i className="wsjtx-dot wsjtx-dot--watch" /> {tx('watchlista')}</label>
            <label className="checkbox-label"><input type="checkbox" checked={!!wsjtx.highlight?.needed}
              onChange={e => setHl({ needed: e.target.checked })} /> <i className="wsjtx-dot wsjtx-dot--needed" /> {tx('brakujące kraje')}</label>
          </div>
          <div className="wsjtx-panel__row">
            {remote && (
              <button type="button" className="btn btn--secondary btn--sm" onClick={openRemote}>
                <Icon name="sliders" size={14} /> {tx('Pełny pilot WSJT-X')}
              </button>
            )}
            <button type="button" className="btn btn--ghost btn--sm" onClick={openDiagnostics}>
              <Icon name="bug" size={14} /> {tx('Diagnostyka')}
            </button>
          </div>
          {!allowed && (
            <div className="wsjtx-panel__hint">
              {tx('Odpowiadanie stacjom i nadawanie tekstu z HamLoggera jest wyłączone. Włączysz je w pliku hamlogger_agent.ini:')} <code>{tx('allow_tx_control = true')}</code> {tx('w sekcji [wsjtx] — tylko gdy nadzorujesz stację.')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
